import {
  Button,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useDisclosure
} from '@chakra-ui/react';
import { useState } from 'react';
import pluralize from 'pluralize';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { SFAddCircleIcon, SFListAddIcon, SFListCheckIcon } from '@assets/icons';
import { useQueryStore, useUserStore } from '@store/app.store';
import { useInitiatives } from '@services/query-services/initiatives.query-service';
import { useBreakpoints, useStyledToast } from '@util/hooks';
import { useSetCardCollections } from '@services/query-services/cards.query-service';
import CreateCollectionModal from '@components/collection/create-collection-modal.component';
import StyledLink from '@components/general/styled-link.component';

/**
 * Used in:
 * 1. Summary Card: to manage collections on single card, pass it as cards={[card]}
 * 2. Recent Feed with applied filters: applied to multiple cards at the same time
 *
 * @param {Object} props
 * @param {Card[]} cards
 * @param {number} opacity
 * @param {any} disclosure
 */
const CollectionsMenu = ({ cards = [], opacity = 1, disclosure, menuButton }) => {
  const user = useUserStore((state) => state.user);
  const [query, setQuery] = useState('');
  const feedCardsSearchBody = useQueryStore((state) => state.feedCardsSearchBody);
  const queryClient = useQueryClient();
  const { data: allCollections } = useInitiatives({ type: 'Collection' });
  const [collectionIdBeingUpdated, setCollectionIdBeingUpdated] = useState(null);
  const createCollectionModalDisclosure = useDisclosure();
  const { mutate: setCardCollections } = useSetCardCollections();
  const toast = useStyledToast();

  const checkCardAddedToCollection = (collection) => {
    return cards?.every((card) => card?.collections?.some((c) => c.id === collection?.id));
  };

  const onToggleCollection = async (collection) => {
    const isCardAddedToCollection = checkCardAddedToCollection(collection);
    setCollectionIdBeingUpdated(collection?.id);
    try {
      for (const card of cards) {
        const cardCollections = card.collections || [];
        const newCardCollections = isCardAddedToCollection
          ? cardCollections?.filter((c) => c?.id !== collection?.id)
          : [...cardCollections, collection];
        setCardCollections({
          id: card?.id,
          ...(newCardCollections?.length > 0 ? { body: newCardCollections } : {}),
          optimisticUpdateValue: newCardCollections
        });

        updateFeedCardCollections(queryClient, card, feedCardsSearchBody, newCardCollections);
        updateCardOnCollectionPage(queryClient, card, collection, isCardAddedToCollection);
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setContext('collection_operation', { collectionId: collection?.id });
        scope.setExtra(
          'error_message',
          `Error ${isCardAddedToCollection ? 'removing' : 'adding'} summaries ${
            isCardAddedToCollection ? 'from' : 'to'
          } Collection`
        );
        Sentry.captureException(e);
      });
      toast({
        title: `Something went wrong while ${
          isCardAddedToCollection ? 'removing' : 'adding'
        } summaries ${isCardAddedToCollection ? 'from' : 'to'} Collection. Please try again.`,
        description: `Desc: ${e}`,
        status: 'error',
        duration: 9000,
        isClosable: true
      });
      return;
    } finally {
      setCollectionIdBeingUpdated(null);
    }

    queryClient.invalidateQueries(['initiatives']);

    toast({
      title: (
        <>
          {pluralize('Summary', cards?.length)}{' '}
          {isCardAddedToCollection ? 'removed from' : 'added to'} Collection{' '}
          <StyledLink
            color='surface.surface '
            to={`/collection/${collection?.id}`}
            textDecor='underline'
          >
            {collection?.title}
          </StyledLink>{' '}
          Successfully.
        </>
      ),
      status: 'info',
      duration: 9000,
      isClosable: true
    });
  };

  return (
    <>
      <Menu
        {...disclosure}
        onClose={() => {
          setQuery('');
          disclosure?.onClose();
        }}
      >
        {menuButton ?? (
          <CollectionsMenuButton
            disclosure={disclosure}
            opacity={opacity}
          />
        )}
        <MenuList
          data-testid='collections-menu'
          as={Stack}
          w='208px'
          gap={2}
          p={3}
          zIndex={5}
        >
          <Text
            variant='label-caps-600-sm'
            color='surface.onSurfaceVariant'
          >
            Add to Collection
          </Text>

          <MenuItem
            iconSpacing={2}
            icon={<SFAddCircleIcon boxSize={4} />}
            onClick={() => createCollectionModalDisclosure.onOpen()}
          >
            <Text variant='label-600-md'>Create a New Collection</Text>
          </MenuItem>

          <Input
            _placeholder={{ fontWeight: 400 }}
            minH={4}
            rounded='full'
            placeholder='Find a collection...'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Stack
            gap={1}
            data-testid='collections-list'
            overflowY='scroll'
            maxH={40}
          >
            {allCollections
              ?.filter(
                (collection) =>
                  isUserAuthorizedToEditCollection(user, collection) &&
                  collection?.title?.toLowerCase().includes(query?.toLowerCase().trim())
              )
              ?.sort((collection) => (checkCardAddedToCollection(collection) ? -1 : 1))
              .map((collection) => {
                const isCollectionBeingUpdated = collectionIdBeingUpdated === collection?.id;
                const isCardAddedToCollection = checkCardAddedToCollection(collection);

                let icon = <SFListAddIcon />;
                if (isCollectionBeingUpdated) {
                  icon = (
                    <Spinner
                      size='xs'
                      m={1}
                    />
                  );
                } else if (isCardAddedToCollection) {
                  icon = <SFListCheckIcon />;
                }

                return (
                  <MenuItem
                    closeOnSelect={false}
                    h={6}
                    key={collection?.id}
                    iconSpacing={2}
                    color={
                      isCardAddedToCollection ? 'brandPrimary.primary' : 'surface.onSurfaceVariant'
                    }
                    icon={icon}
                    onClick={() => onToggleCollection(collection)}
                  >
                    <Text variant='label-600-md'>{collection?.title}</Text>
                  </MenuItem>
                );
              })}
          </Stack>
        </MenuList>
      </Menu>
      <CreateCollectionModal
        cards={cards}
        disclosure={createCollectionModalDisclosure}
      />
    </>
  );
};

const isUserAuthorizedToEditCollection = (user, collection) => {
  const hasEditorAccess = collection?.collaborators?.some(
    (collaborator) => collaborator.id === user?.id && collaborator.access === 'Editor'
  );
  const isCreator = collection?.creator?.id === user?.id;

  return isCreator || hasEditorAccess;
};

const CollectionsMenuButton = ({ opacity, disclosure }) => {
  const { isMobile } = useBreakpoints();

  const sharedProps = {
    'data-testid': 'collections-menu-button',
    variant: 'reskinGhost',
    colorScheme: 'surface',
    size: 'reskinSm',
    onClick: () => disclosure.onToggle(),
    opacity
  };

  const icon = <SFListAddIcon />;

  const button = (
    <MenuButton
      {...sharedProps}
      as={Button}
      leftIcon={icon}
      iconSpacing={{ base: 0.5, md: 1 }}
      px={{ base: 1, md: 2 }}
    >
      <Text variant='label-reg-md'>Collections</Text>
    </MenuButton>
  );

  const iconButton = (
    <MenuButton
      {...sharedProps}
      as={IconButton}
      boxSize={7}
      isRound
      icon={icon}
      transitionDuration='75ms'
    />
  );

  return (
    <Tooltip
      label='Add to Collection'
      isDisabled={isMobile || opacity === 0}
      openDelay={500}
      hasArrow
    >
      {isMobile ? button : iconButton}
    </Tooltip>
  );
};

/** adds or removes card from collection page */
const updateCardOnCollectionPage = (queryClient, card, collection, isCardAddedToCollection) => {
  const queryKey = ['cards', { collection_ids: [collection?.id] }];
  const queryData = queryClient.getQueryData(queryKey);
  if (!queryData) {
    return;
  }

  if (!isCardAddedToCollection) {
    queryData.pages[0].data = [card, ...queryData.pages[0].data];
    queryClient.setQueryData(queryKey, queryData);
    return;
  }

  for (let pageIndex = 0; pageIndex < queryData.pages.length; pageIndex++) {
    const cardIndex = queryData.pages[pageIndex].data.findIndex((c) => card?.id === c.id);
    if (cardIndex === -1) {
      continue;
    }
    queryData.pages[pageIndex].data.splice(cardIndex, 1);
    break;
  }
  queryClient.setQueryData(queryKey, queryData);
};

/** updates feed cards with new collection array */
const updateFeedCardCollections = (queryClient, card, feedCardsSearchBody, newCardCollections) => {
  const queryKey = ['cards', feedCardsSearchBody];
  const queryData = queryClient.getQueryData(queryKey);
  if (!queryData) {
    return;
  }

  for (let pageIndex = 0; pageIndex < queryData.pages.length; pageIndex++) {
    const cardIndex = queryData.pages[pageIndex].data.findIndex((c) => card?.id === c.id);
    if (cardIndex === -1) {
      continue;
    }
    if (newCardCollections?.length > 0) {
      queryData.pages[pageIndex].data[cardIndex].collections = newCardCollections;
    } else {
      delete queryData.pages[pageIndex].data[cardIndex].collections;
    }
    break;
  }
  queryClient.setQueryData(queryKey, queryData);
};

export default CollectionsMenu;
